/**
 * Hell oder dunkel für die Besucher-App (ADR 0011).
 *
 * **Die App folgt dem System** (Flo, 05.10.): Steht das Handy auf hell, ist
 * K4ia hell; steht es auf dunkel, ist sie dunkel — und wechselt mit, wenn das
 * System umschaltet (abends automatisch dunkel). Der Schalter oben rechts
 * bleibt: Wer ihn tippt, legt sich fest, und diese Wahl gilt in diesem Browser
 * vor dem System, bis der Speicher geleert wird.
 *
 * Das Skript in index.html wendet dieselbe Regel vor dem ersten Zeichnen an,
 * damit nichts flackert — STORAGE_KEY und die Regel dort in Sync halten.
 */
export type Theme = 'dark' | 'light'

export const STORAGE_KEY = 'k4ia_theme'
/** Gilt nur, wenn weder eine Wahl gespeichert ist noch das System etwas sagt. */
export const DEFAULT_THEME: Theme = 'dark'

/** Browser chrome colour per theme (matches --color-bg). */
const THEME_COLOR: Record<Theme, string> = { dark: '#000000', light: '#f5f5f7' }

const SYSTEM_QUERY = '(prefers-color-scheme: dark)'

/** Die im Browser gespeicherte Wahl des Besuchers — oder null, wenn er dem System folgt. */
export function storedTheme(): Theme | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored === 'light' || stored === 'dark' ? stored : null
  } catch {
    // Private mode or blocked storage: no stored choice.
    return null
  }
}

/** Was das System gerade vorgibt. */
export function systemTheme(): Theme {
  try {
    return window.matchMedia(SYSTEM_QUERY).matches ? 'dark' : 'light'
  } catch {
    return DEFAULT_THEME
  }
}

/** Gespeicherte Wahl vor System vor Vorgabe. */
export function readTheme(): Theme {
  return storedTheme() ?? systemTheme()
}

/**
 * Setzt das Theme. `merken` speichert es als Wahl des Besuchers (Schalter);
 * ohne `merken` folgt die App weiter dem System (Start, Systemwechsel).
 */
export function applyTheme(theme: Theme, merken = true): void {
  document.documentElement.dataset.theme = theme
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme])
  if (!merken) return
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // Not persisted — the switch still works for this page view.
  }
}

/**
 * Folgt dem System, solange keine eigene Wahl gespeichert ist: wendet den
 * Stand beim Start an und reagiert auf Umschalten (etwa abends auf dunkel).
 * Gibt die Funktion zum Abmelden zurück.
 */
export function followSystemTheme(onChange?: (theme: Theme) => void): () => void {
  applyTheme(readTheme(), false)
  let media: MediaQueryList | null = null
  try {
    media = window.matchMedia(SYSTEM_QUERY)
  } catch {
    return () => {}
  }
  const listener = () => {
    if (storedTheme()) return
    const theme = systemTheme()
    applyTheme(theme, false)
    onChange?.(theme)
  }
  media.addEventListener?.('change', listener)
  return () => media?.removeEventListener?.('change', listener)
}
